import React, { createContext } from 'react';
import type { QnAStateType } from '@uber/react-qna-lib';
import type { BoundingBoxTrackingInputT, BoundingBoxTrackingOutputT, Sam3TextPromptRequestT, SamModelImagePluginRequestT, SamModelPluginRequestT, SAMSegmentImageData, SAMSegmentVideoData, TrackObjectsOutputT, TrackObjectsRequestT } from '../../image/types';
import type { ForceAlignment } from '../../transcription/types/force-alignment';
import type { WebWorker } from '../../workers/types/common';
import type { ValidateTextRequestT, ValidateTextResponseT } from '../text-validation/types';
import type { ValidateTaskSOPRequestT, ValidateTaskSOPResponseT } from '../types/sop-adherence';
import type { GetSignedUrlT, RegisterHotkeysArgsT, RegisterHotkeysResultT } from '../types/types';
import type { GetULabelConfigFunction } from './feature-flag-provider';
type APIContextT = {
  getSignedUrl?: GetSignedUrlT;
  getSAMImageDataPoints?: (input: SamModelImagePluginRequestT) => Promise<SAMSegmentImageData[]>;
  getSAM3ImageDataPoints?: (input: Sam3TextPromptRequestT) => Promise<SAMSegmentImageData[]>;
  getSamSegmentData?: (input: SamModelPluginRequestT, labelTitle?: string) => Promise<SAMSegmentImageData | Error | null>;
  getAnnotationTrackingData?: (labelTitle: string, frameRange?: {
    start: number;
    end: number;
  }) => Promise<SAMSegmentVideoData | Error>;
  getBoundingBoxData?: (input: BoundingBoxTrackingInputT) => Promise<BoundingBoxTrackingOutputT | Error>;
  trackObjectsAcrossKeyframes?: (input: TrackObjectsRequestT) => Promise<TrackObjectsOutputT | Error>;
  fetchFileSizeAndFormat?: (src: string) => Promise<{
    fileSize: number | null;
    format: string | null;
  }>;
  fetchQuestionnaire: (questionnaireId: string) => Promise<QnAStateType>;
  validateText?: (request: ValidateTextRequestT) => Promise<ValidateTextResponseT>;
  validateTaskSOP?: (request: ValidateTaskSOPRequestT) => Promise<ValidateTaskSOPResponseT>;
  registerHotKeys?: (args: RegisterHotkeysArgsT) => RegisterHotkeysResultT;
  getULabelConfig?: GetULabelConfigFunction;
  videoFrameProcessor?: WebWorker;
  forceAlignment?: ForceAlignment;
  openInstructions?: () => void;
};
type Props = React.PropsWithChildren<{
  getSignedUrl?: GetSignedUrlT;
  getSAMImageDataPoints?: (input: SamModelImagePluginRequestT) => Promise<SAMSegmentImageData[]>;
  getSAM3ImageDataPoints?: (input: Sam3TextPromptRequestT) => Promise<SAMSegmentImageData[]>;
  getSamSegmentData?: (input: SamModelPluginRequestT, labelTitle?: string) => Promise<SAMSegmentImageData | Error | null>;
  getAnnotationTrackingData?: (labelTitle: string, frameRange?: {
    start: number;
    end: number;
  }) => Promise<SAMSegmentVideoData | Error>;
  getBoundingBoxData?: (input: BoundingBoxTrackingInputT) => Promise<BoundingBoxTrackingOutputT | Error>;
  trackObjectsAcrossKeyframes?: (input: TrackObjectsRequestT) => Promise<TrackObjectsOutputT | Error>;
  fetchFileSizeAndFormat?: (src: string) => Promise<{
    fileSize: number | null;
    format: string | null;
  }>;
  fetchQuestionnaire: (questionnaireId: string) => Promise<QnAStateType>;
  validateText?: (request: ValidateTextRequestT) => Promise<ValidateTextResponseT>;
  validateTaskSOP?: (request: ValidateTaskSOPRequestT) => Promise<ValidateTaskSOPResponseT>;
  registerHotKeys?: (args: RegisterHotkeysArgsT) => RegisterHotkeysResultT;
  getULabelConfig?: GetULabelConfigFunction;
  videoFrameProcessor?: WebWorker;
  forceAlignment?: ForceAlignment;
  openInstructions?: () => void;
}>;
export const APIContext = createContext<APIContextT>({
  getSignedUrl: undefined,
  getSamSegmentData: undefined,
  getSAMImageDataPoints: undefined,
  getSAM3ImageDataPoints: undefined,
  getAnnotationTrackingData: undefined,
  getBoundingBoxData: undefined,
  trackObjectsAcrossKeyframes: undefined,
  fetchFileSizeAndFormat: undefined,
  fetchQuestionnaire: () => Promise.resolve({
    questions: {},
    topQuestionIds: [],
    answers: {}
  }),
  validateText: undefined,
  validateTaskSOP: undefined,
  registerHotKeys: undefined,
  getULabelConfig: undefined,
  videoFrameProcessor: undefined,
  forceAlignment: undefined,
  openInstructions: undefined
});
export const APIContextProvider = ({
  getSignedUrl,
  getAnnotationTrackingData,
  getSamSegmentData,
  getSAMImageDataPoints,
  getSAM3ImageDataPoints,
  getBoundingBoxData,
  trackObjectsAcrossKeyframes,
  fetchFileSizeAndFormat,
  fetchQuestionnaire,
  validateText,
  validateTaskSOP,
  registerHotKeys,
  getULabelConfig,
  forceAlignment,
  videoFrameProcessor,
  openInstructions,
  children
}: Props) => {
  const contextValue = React.useMemo(() => ({
    getSignedUrl,
    getAnnotationTrackingData,
    getSamSegmentData,
    getSAMImageDataPoints,
    getSAM3ImageDataPoints,
    getBoundingBoxData,
    trackObjectsAcrossKeyframes,
    fetchFileSizeAndFormat,
    fetchQuestionnaire,
    validateText,
    validateTaskSOP,
    registerHotKeys,
    getULabelConfig,
    videoFrameProcessor,
    forceAlignment,
    openInstructions
  }), [getSignedUrl, getAnnotationTrackingData, getSamSegmentData, getSAMImageDataPoints, getSAM3ImageDataPoints, getBoundingBoxData, trackObjectsAcrossKeyframes, fetchFileSizeAndFormat, fetchQuestionnaire, validateText, validateTaskSOP, registerHotKeys, getULabelConfig, videoFrameProcessor, forceAlignment, openInstructions]);
  return <APIContext.Provider value={contextValue}>{children}</APIContext.Provider>;
};